import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { Check, X, AlertCircle, User, BellRing, Power, DollarSign, Star, MessageCircle, Clock, Send, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import RatingModal from './RatingModal';
import VoiceCallWindow from '../VoiceCallWindow';

interface ChatInterfaceProps {
  conversationId?: string;
  clientId?: string;
  advisorId?: string;
  advisor?: any;
  isClient: boolean;
  onClose: () => void;
}

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  created_at: string;
}

export default function ChatInterface({
  conversationId,
  clientId,
  advisorId,
  advisor,
  isClient,
  onClose
}: ChatInterfaceProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversation, setConversation] = useState<any>(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [messagePrice, setMessagePrice] = useState<number>(0);
  const [freeMessagesRemaining, setFreeMessagesRemaining] = useState<number>(0);
  const [freeMessagesUsed, setFreeMessagesUsed] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const channelRef = useRef<any>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showVoiceCallWindow, setShowVoiceCallWindow] = useState(false);
  const [hasExistingReview, setHasExistingReview] = useState(false);
  const [chatEnded, setChatEnded] = useState(false);
  const [chatEndedBy, setChatEndedBy] = useState<'client' | 'advisor' | null>(null);

  // Determine the current user's ID and the other participant's ID
  const currentUserId = user?.id;
  const otherUserId = isClient ? advisorId : clientId;

  useEffect(() => {
    if (!user) return;
    
    if (conversationId) {
      fetchConversation();
      fetchMessages();
      subscribeToMessages();
      
      if (isClient) {
        fetchWalletBalance();
        fetchMessagePrice();
        fetchFreeMessagesRemaining();
      }
    } else if (advisor && isClient) {
      createConversation();
    }

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
      }
    };
  }, [user, conversationId, advisor]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversation = async () => {
    try {
      if (!conversationId) return;
      
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          id,
          client_id,
          advisor_id,
          status,
          created_at,
          updated_at,
          clients:client_id(display_name, profile_image_url),
          advisors:advisor_id(display_name, profile_image_url, per_message_rate)
        `)
        .eq('id', conversationId)
        .single();

      if (error) {
        throw error;
      }

      setConversation(data);
      
      // Check if conversation is closed
      if (data.status === 'closed') {
        setChatEnded(true);
      }
      
      if (isClient && data.advisors) {
        setMessagePrice(data.advisors.per_message_rate || 0);
      }
    } catch (err) {
      console.error('Error fetching conversation:', err);
      setError('Failed to load conversation details');
    }
  };

  const fetchMessages = async () => {
    try {
      setLoading(true);
      
      if (!conversationId) return;

      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) {
        throw error;
      }

      setMessages(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const subscribeToMessages = () => {
    if (!conversationId) return;
    
    const channel = supabase.channel(`messages-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          console.log('New message received:', payload);
          const newMessage = payload.new as Message;
          setMessages(prevMessages => [...prevMessages, newMessage]);
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    channelRef.current = channel;
  };

  const createConversation = async () => {
    try {
      setLoading(true);
      
      if (!user || !advisor) return;

      // Create a new conversation
      const { data, error } = await supabase
        .from('conversations')
        .insert({
          client_id: user.id,
          advisor_id: advisor.id,
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        // If the error is a unique constraint violation, try to get the existing conversation
        if (error.code === '23505') {
          const { data: existingConversation, error: fetchError } = await supabase
            .from('conversations')
            .select('*')
            .eq('client_id', user.id)
            .eq('advisor_id', advisor.id)
            .eq('status', 'pending')
            .single();
            
          if (fetchError) {
            throw fetchError;
          }
          
          setConversation(existingConversation);
        } else {
          throw error;
        }
      } else {
        setConversation(data);
      }
      
      setMessagePrice(advisor.per_message_rate || 0);
      fetchWalletBalance();
      fetchFreeMessagesRemaining();
    } catch (error) {
      console.error('Error creating conversation:', error);
      setError('Failed to start conversation');
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      if (!user) return;
      
      const { data, error } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching wallet balance:', error);
        return;
      }

      setWalletBalance(data?.balance || 0);
    } catch (err) {
      console.error('Error fetching wallet balance:', err);
    }
  };

  const fetchMessagePrice = async () => {
    try {
      if (!advisorId) return;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('per_message_rate')
        .eq('id', advisorId)
        .single();

      if (error) {
        console.error('Error fetching message price:', error);
        return;
      }

      setMessagePrice(data?.per_message_rate || 0);
    } catch (err) {
      console.error('Error fetching message price:', err);
    }
  };

  const fetchFreeMessagesRemaining = async () => {
    try {
      if (!user) return;
      
      const { data, error } = await supabase
        .from('message_credits')
        .select('credits_remaining, free_messages_used')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching free messages:', error);
        return;
      }

      setFreeMessagesRemaining(data?.credits_remaining || 0);
      setFreeMessagesUsed(data?.free_messages_used || false);
    } catch (err) {
      console.error('Error fetching free messages:', err);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim() || sending) return;
    
    if (!user || !conversation) {
      setError('Unable to send message. Please try again.');
      return;
    }
    
    // Check if conversation is active
    if (conversation.status !== 'active') {
      if (isClient) {
        setError('Please wait for the advisor to accept your chat request.');
      } else {
        // If advisor is sending the first message, update conversation status to active
        try {
          const { error: updateError } = await supabase
            .from('conversations')
            .update({ status: 'active' })
            .eq('id', conversation.id);
            
          if (updateError) {
            throw updateError;
          }
          
          // Update local state
          setConversation({
            ...conversation,
            status: 'active'
          });
        } catch (err) {
          console.error('Error updating conversation status:', err);
          setError('Failed to activate conversation');
          return;
        }
      }
    }
    
    // For clients, check if they have enough balance or free messages
    if (isClient && messagePrice > 0) {
      const hasFreeMessages = freeMessagesRemaining > 0 && !freeMessagesUsed;
      const hasEnoughBalance = walletBalance >= messagePrice;
      
      if (!hasFreeMessages && !hasEnoughBalance) {
        setError(`Insufficient funds. Please add funds to your wallet. Message price: $${messagePrice.toFixed(2)}`);
        return;
      }
    }
    
    try {
      setSending(true);
      setError(null);
      
      // Prepare message data
      const messageData = {
        conversation_id: conversation.id,
        sender_id: user.id,
        receiver_id: isClient ? conversation.advisor_id : conversation.client_id,
        content: newMessage,
        price: isClient ? messagePrice : 0
      };
      
      // Send message
      const { data, error } = await supabase
        .from('messages')
        .insert(messageData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Clear input
      setNewMessage('');
      
      // If client sent a message, update wallet balance and free messages
      if (isClient) {
        // Refresh wallet balance and free messages
        fetchWalletBalance();
        fetchFreeMessagesRemaining();
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const checkExistingReview = async () => {
    if (!user || !conversation) return false;
    
    try {
      const { data, error } = await supabase
        .from('advisor_ratings')
        .select('id')
        .eq('advisor_id', conversation.advisor_id)
        .eq('client_id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      return !!data; // Return true if a review exists, false otherwise
    } catch (error) {
      console.error('Error checking existing review:', error);
      return false;
    }
  };

  const handleEndChat = async () => {
    try {
      if (!conversation) return;
      
      console.log("Ending chat with ID:", conversation.id);
      
      // First, update the conversation status to closed
      const { error } = await supabase
        .from('conversations')
        .update({ status: 'closed' })
        .eq('id', conversation.id);

      if (error) {
        throw error;
      }

      // Set chat as ended and record who ended it
      setChatEnded(true);
      setChatEndedBy(isClient ? 'client' : 'advisor');
      
      // Update conversation state
      setConversation({
        ...conversation,
        status: 'closed'
      });

      // After updating conversation status to closed
      // Add a delay to ensure database updates complete
      setTimeout(async () => {
        try {
          console.log('Calling reset_advisor_status function for advisor:', conversation.advisor_id);
          
          // Call the database function to reset advisor status with the correct parameter name
          const { error } = await supabase.rpc('reset_advisor_status', {
            advisor_id: conversation.advisor_id
          });
          
          if (error) {
            console.error('Error resetting advisor status:', error);
          } else {
            console.log('Successfully reset advisor status');
          }
        } catch (err) {
          console.error('Error calling reset_advisor_status:', err);
        }
      }, 2000);
      
      // Check if client has already left a review (but don't show modal automatically)
      if (isClient) {
        const hasReview = await checkExistingReview();
        setHasExistingReview(hasReview);
      }
      
      // Note: We don't call onClose() here to keep the chat window open
    } catch (err) {
      console.error('Error ending chat:', err);
      setError('Failed to end chat');
    }
  };

  const handleRatingSubmit = () => {
    // Close the rating modal but keep the chat window open
    setShowRatingModal(false);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e as unknown as React.FormEvent);
    }
  };

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [newMessage]);

  if (loading && !conversation) {
    return (
      <div className="flex flex-col h-full items-center justify-center bg-gray-50 p-4">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin mb-4" />
        <p className="text-gray-600">Loading conversation...</p>
      </div>
    );
  }

  if (showVoiceCallWindow) {
    return (
      <VoiceCallWindow
        conversationId={conversation?.id || ''}
        clientId={conversation?.client_id || ''}
        advisorId={conversation?.advisor_id || ''}
        isClient={isClient}
        onClose={() => setShowVoiceCallWindow(false)}
        advisorName={conversation?.advisors?.display_name}
        clientName={conversation?.clients?.display_name}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="bg-purple-600 text-white p-4 flex justify-between items-center">
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center overflow-hidden">
            {isClient ? (
              conversation?.advisors?.profile_image_url ? (
                <img 
                  src={conversation.advisors.profile_image_url} 
                  alt={conversation.advisors.display_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-6 h-6 text-white" />
              )
            ) : (
              conversation?.clients?.profile_image_url ? (
                <img 
                  src={conversation.clients.profile_image_url} 
                  alt={conversation.clients.display_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-6 h-6 text-white" />
              )
            )}
          </div>
          <div className="ml-3">
            <p className="font-medium">
              {isClient 
                ? conversation?.advisors?.display_name || 'Advisor'
                : conversation?.clients?.display_name || 'Client'
              }
            </p>
            <p className="text-xs text-purple-200">
              {chatEnded ? 'Chat Ended' : conversation?.status === 'active' ? 'Active Chat' : 'Pending'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {!chatEnded && (
            <button
              onClick={handleEndChat}
              className="flex items-center bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
              title="End Chat"
            >
              <span>End Chat</span>
            </button>
          )}
          {chatEnded && (
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-purple-500 hover:bg-purple-700 transition-colors"
              title="Close Chat Window"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <p className="mb-2">No messages yet</p>
            {conversation?.status === 'pending' && isClient && (
              <p className="text-sm">Waiting for advisor to accept your chat request...</p>
            )}
            {conversation?.status === 'pending' && !isClient && (
              <p className="text-sm">Accept this chat to start messaging with the client</p>
            )}
            {conversation?.status === 'active' && (
              <p className="text-sm">Start the conversation by sending a message</p>
            )}
            {chatEnded && (
              <div className="text-center mt-4">
                <p className="text-purple-600 font-medium mb-2">
                  {chatEndedBy === 'advisor' 
                    ? 'The advisor has ended this chat session.' 
                    : 'You have ended this chat session.'}
                </p>
                {isClient && (
                  <button
                    onClick={() => setShowRatingModal(true)}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors mt-2"
                  >
                    <Star className="w-4 h-4 inline mr-2" />
                    Leave a Review
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender_id === currentUserId ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.sender_id === currentUserId
                      ? 'bg-purple-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-800'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{message.content}</p>
                  <div
                    className={`text-xs mt-1 ${
                      message.sender_id === currentUserId ? 'text-purple-200' : 'text-gray-500'
                    }`}
                  >
                    {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
            
            {/* Chat ended message */}
            {chatEnded && (
              <div className="flex justify-center my-4">
                <div className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm">
                  {chatEndedBy === 'advisor' 
                    ? 'The advisor has ended this chat session.' 
                    : 'You have ended this chat session.'}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Message Input */}
      <div className="border-t bg-white">
        {/* Error message at the bottom */}
        {error && (
          <div className="p-3 bg-red-50 border-t border-red-200 flex items-center justify-between text-red-700">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <p>{error}</p>
            </div>
            {isClient && error.includes('Insufficient funds') && (
              <button
                onClick={() => navigate('/client/dashboard')}
                className="ml-2 bg-red-600 text-white px-3 py-1 rounded-md text-sm hover:bg-red-700"
              >
                Add Funds
              </button>
            )}
          </div>
        )}
        
        {/* Message price and wallet balance */}
        {isClient && (
          <div className="flex justify-between text-xs text-gray-500 p-2 px-4">
            <span>
              {freeMessagesRemaining > 0 && !freeMessagesUsed
                ? `${freeMessagesRemaining} free ${freeMessagesRemaining === 1 ? 'message' : 'messages'} remaining` 
                : `Message price: $${messagePrice.toFixed(2)}`
              }
            </span>
            <span className="flex items-center">
              <DollarSign className="w-3 h-3 mr-1" />
              Wallet balance: ${walletBalance.toFixed(2)}
            </span>
          </div>
        )}
        
        {/* Message input form */}
        <form onSubmit={handleSendMessage} className="flex items-end p-4 pt-2">
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={chatEnded ? "Chat has ended" : "Type your message..."}
            className="flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none min-h-[40px] max-h-[120px]"
            disabled={conversation?.status !== 'active' || sending || chatEnded}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending || conversation?.status !== 'active' || chatEnded}
            className="ml-2 bg-purple-600 text-white rounded-full p-2 hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>
        
        {/* Review button for clients when chat has ended */}
        {chatEnded && isClient && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="text-center mb-3 text-gray-600">
              <p>Thank you for chatting with us!</p>
            </div>
            <button
              onClick={() => setShowRatingModal(true)}
              className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
            >
              <Star className="w-5 h-5 mr-2" />
              {hasExistingReview ? 'Update Your Review' : 'Leave a Review'}
            </button>
          </div>
        )}
      </div>

      {/* Rating Modal */}
      {showRatingModal && (
        <RatingModal 
          advisorId={conversation.advisor_id}
          onClose={() => setShowRatingModal(false)}
          onRatingSubmit={handleRatingSubmit}
        />
      )}
    </div>
  );
}